/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-controls-child-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';

import { useTranslation, useGridCells } from '../../../hooks';
import ExpandableGridV2 from '../core/expandable/grid-v2';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from '../core/grid-filters';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';

const AdminControlsChildGrid = ({ isNested, associatedControls, rowActions, isSystemLevel }: any) => {
    const { t } = useTranslation();

    const { MaxLengthCell, YesNoCell, DateCell, CustomizableCell, ActiveInactiveCell } = useGridCells();
    const [data, setData] = useState(null);
    const [controls, setControls] = useState(null);

    useEffect(() => {
        if (data) {
            const newControls = data?.map((item: any) => ({
                ...item,
                _phaseLabel: item.phase ? t(`ra:${item.phase}`) : '',
            }));

            setControls(newControls);
        }
    }, [data]);

    useEffect(() => {
        const filteredData = [...associatedControls];
        const newData = restructuredUpdatedCreatedBy(filteredData);

        setData(
            orderBy(newData, [
                { field: 'active', dir: 'desc' },
                { field: 'updated', dir: 'desc' },
            ]),
        );
    }, [associatedControls]);

    return (
        <div
            className={isNested ? 'child-grid-wrap control-ismp-grid' : 'control-ismp-grid'}
            key="admin-controls-grid-wrapper"
        >
            <ExpandableGridV2
                title={t('ra:controls')}
                isChildGrid
                customKey="control-ismp-grid"
                items={controls}
                groupable={false}
                showPager
                hideCheckbox
                hideExpand
                scrollable="scrollable"
                reorderable
                resizable
                pageable={false}
                rowActions={rowActions}
                columns={[
                    {
                        label: t('ra:description'),
                        id: 'control-description',
                        field: 'description',
                        minWidth: 180,
                        lockedByDefault: true,
                        cells: { data: MaxLengthCell(150, '30vh') },
                    },
                    {
                        label: t('ra:phase'),
                        id: 'control-phase',
                        minWidth: 140,
                        field: '_phaseLabel',
                        cells: { data: MaxLengthCell(17) },
                    },
                    ...(!isSystemLevel
                        ? [
                              {
                                  label: t('admin:controlType'),
                                  id: 'control-type',
                                  minWidth: 200,
                                  field: 'controlType.name',
                                  cells: {
                                      data: ActiveInactiveCell(
                                          (dataItem: any) => dataItem.controlType?.active === false,
                                          (dataItem: any) => dataItem.controlType?.name,
                                      ),
                                  },
                              },
                          ]
                        : []),
                    {
                        label: t('ra:threshold'),
                        id: 'control-threshold',
                        field: 'threshold',
                        minWidth: 170,
                        cells: { data: MaxLengthCell(150) },
                    },
                    {
                        label: t('ra:actionToBeTaken'),
                        id: 'control-action-to-be-taken',
                        minWidth: 210,
                        field: 'action',
                        cells: { data: MaxLengthCell(150) },
                    },
                    ...(!isSystemLevel
                        ? [
                              {
                                  label: t('ra:responsibleFunction'),
                                  id: 'control-responsible-func',
                                  field: 'responsibleFunction.name',
                                  minWidth: 260,
                                  cells: {
                                      data: ActiveInactiveCell(
                                          (dataItem: any) => dataItem.responsibleFunction?.active === false,
                                          (dataItem: any) => dataItem.responsibleFunction?.name,
                                      ),
                                  },
                              },
                          ]
                        : []),
                    {
                        label: t('ra:reviewFrequency'),
                        id: 'control-review-frequency',
                        minWidth: 220,
                        field: '_reviewFrequencyMS',
                        filterKey: '_reviewFrequency',
                        cells: { data: CustomizableCell((dataItem: any) => dataItem._reviewFrequency || '') },
                    },
                    {
                        label: t('active'),
                        id: 'control-active',
                        field: 'active',
                        minWidth: 140,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        label: t('ra:createdBy'),
                        id: 'control-createdby',
                        field: '_createdByString',
                        minWidth: 180,
                    },
                    {
                        label: t('ra:createdDate'),
                        id: 'control-created',
                        minWidth: 190,
                        field: 'created',
                        filter: 'date',
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilter,
                    },
                    {
                        label: t('ra:updatedBy'),
                        id: 'control-updatedby',
                        field: '_updatedByString',
                        minWidth: 180,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        id: 'control-updated',
                        filter: 'date',
                        minWidth: 190,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilter,
                    },
                    {
                        field: 'recordId',
                        id: 'control-id',
                        showByDefault: false,
                        label: 'ID',
                        filter: 'numeric',
                        minWidth: 120,
                    },
                ]}
            />
        </div>
    );
};

export default AdminControlsChildGrid;
